import {
  SiteStore,
  StoreMetaDataApi,
  PurchaseFlowPropertiesService,
  IPurchaseFlowProperties,
} from '@wix/wixstores-client-storefront-sdk';
import {CART_ORIGIN, PAYPAL_METHOD_NAME, PaymentMethodType} from '../../common/constants';
import {PaymentMethod} from '@wix/wixstores-graphql-schema-node';
import {SPECS} from '../specs';

export class StoreMetaDataService {
  private readonly storeMetaDataApi: StoreMetaDataApi;
  private readonly siteStore: SiteStore;
  private readonly purchaseFlowPropertiesService?: PurchaseFlowPropertiesService;
  private readonly _storeMetaDataPromise: ReturnType<StoreMetaDataApi['getStoreMetaData']>;
  private readonly purchaseFlowPropertiesPromise?: Promise<IPurchaseFlowProperties>;

  constructor({
    siteStore,
    purchaseFlowPropertiesService,
  }: {
    siteStore: SiteStore;
    purchaseFlowPropertiesService?: PurchaseFlowPropertiesService;
  }) {
    this.purchaseFlowPropertiesService = purchaseFlowPropertiesService;
    this.siteStore = siteStore;
    this.storeMetaDataApi = new StoreMetaDataApi({siteStore, origin: CART_ORIGIN});

    if (
      this.purchaseFlowPropertiesService &&
      this.siteStore.experiments.enabled(SPECS.ShouldPreloadPurchaseFlowProperties)
    ) {
      this.purchaseFlowPropertiesPromise = this.purchaseFlowPropertiesService.getPurchaseFlowProperties();
    } else {
      this._storeMetaDataPromise = this.storeMetaDataApi.getStoreMetaData();
    }
  }

  public async get(): Promise<ReturnType<StoreMetaDataApi['getStoreMetaData']>> {
    if (this.purchaseFlowPropertiesPromise !== undefined) {
      return (await this.purchaseFlowPropertiesPromise).storeMetaData;
    }

    return this._storeMetaDataPromise;
  }

  private async getActivePaymentMethods() {
    const storeMetaData = await this.get();
    return storeMetaData?.activePaymentMethods ?? [];
  }

  private async getRegularFlowPaymentMethods(): Promise<PaymentMethod[]> {
    const activePaymentMethods = await this.getActivePaymentMethods();
    return activePaymentMethods.filter(({name}) => name !== PAYPAL_METHOD_NAME);
  }

  private async getEWalletPaymentMethods(): Promise<PaymentMethod[]> {
    const activePaymentMethods = await this.getActivePaymentMethods();
    return activePaymentMethods.filter((paymentMethod) => paymentMethod.type === PaymentMethodType.eWallet);
  }

  private async getOfflinePaymentMethods(): Promise<PaymentMethod[]> {
    const activePaymentMethods = await this.getActivePaymentMethods();
    return activePaymentMethods.filter((paymentMethod) => paymentMethod.type === PaymentMethodType.offline);
  }

  public async hasRegularFlowPaymentMethods() {
    return !!(await this.getRegularFlowPaymentMethods()).length;
  }

  public async hasAnyPaymentMethods() {
    return !!(await this.getActivePaymentMethods()).length;
  }

  public async hasEWalletPaymentMethods() {
    return !!(await this.getEWalletPaymentMethods()).length;
  }

  public async hasOfflinePaymentMethods() {
    return !!(await this.getOfflinePaymentMethods()).length;
  }

  public async getPaymentMethodsNames() {
    const activePaymentMethods = await this.getActivePaymentMethods();
    return activePaymentMethods.map((method) => method.name);
  }
}
